// An idea written out in plain text, for the producer (Claude, in the studio's composer) to read: a
// drum or snare part as the assistant's step grids (gridOf: the form Claude writes a new version in,
// see services/studio/compose.py's write_idea), other drums and a tune as their notes, bar by bar. At
// most four bars: enough to know the idea, short enough to read.
import { scoreEvents } from "../graph/midi";
import { gridOf } from "../assistant/grooves";

const DRUM_NAMES = { 35: "kick", 36: "kick", 37: "side stick", 38: "snare", 40: "snare", 41: "floor tom", 42: "hi-hat", 43: "floor tom", 44: "hi-hat foot",
  45: "low tom", 46: "open hi-hat", 47: "mid tom", 48: "high tom", 49: "crash", 50: "high tom", 51: "ride", 57: "crash" };
const NOTE_NAMES = ["C", "C#", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];
const noteName = (pitch) => `${NOTE_NAMES[pitch % 12]}${Math.floor(pitch / 12) - 1}`;
const BARS = 4, STEPS_PER_QUARTER = 4;

export function describeScore(score, tempo = 90) {
  const measures = score?.measures || [];
  if (!measures.length) return "";
  // Where each of the first bars starts and how long it is, in quarter notes.
  const bars = [];
  let at = 0;
  for (const measure of measures.slice(0, BARS)) { const length = measure.timeSig.num * 4 / measure.timeSig.type; bars.push({ at, length }); at += length; }
  const end = at, events = scoreEvents({ ...score, measures: measures.slice(0, BARS) }, tempo).filter((event) => event.at < end - 1e-6);
  const drums = events.filter((event) => event.channel === 9), notes = events.filter((event) => event.channel !== 9);
  const lines = [];
  // Drum set and snare parts as grids; marching drums, which grids don't write, as hits by name.
  const parts = new Set(measures.slice(0, BARS).flatMap((measure) => measure.parts.map((part) => part.instrument)));
  if (drums.length && [...parts].every((part) => part === "drumset" || part === "snare" || part === "piano")) {
    lines.push(gridOf({ ...score, measures: measures.slice(0, BARS) }, { bars: BARS }).split("\n").filter((line) => !line.startsWith("piano part")).join("\n"));
  } else if (drums.length) {
    const rows = new Map();
    for (const event of drums) {
      const name = DRUM_NAMES[event.pitch] || `drum ${event.pitch}`;
      if (!rows.has(name)) rows.set(name, bars.map((bar) => Array(Math.round(bar.length * STEPS_PER_QUARTER)).fill(".")));
      const index = bars.findIndex((bar) => event.at >= bar.at - 1e-6 && event.at < bar.at + bar.length - 1e-6), bar = bars[index];
      const step = Math.min(Math.round((event.at - bar.at) * STEPS_PER_QUARTER), Math.round(bar.length * STEPS_PER_QUARTER) - 1);
      const mark = event.velocity >= 0.9 ? "X" : event.velocity <= 0.3 ? "g" : "x", cells = rows.get(name)[index];
      if (cells[step] !== "X") cells[step] = mark === "x" && cells[step] === "g" ? "x" : mark;
    }
    lines.push(`Drums, sixteenths across each bar (x hit, X accent, g ghost), ${bars.length} bar${bars.length > 1 ? "s" : ""}:`);
    for (const [name, cells] of rows) lines.push(`${name.padEnd(11)} ${cells.map((bar) => bar.join("")).join(" | ")}`);
  }
  if (notes.length) {
    lines.push(`Notes, bar by bar (lowest of each chord first):`);
    bars.forEach((bar, index) => {
      const inBar = notes.filter((event) => event.at >= bar.at - 1e-6 && event.at < bar.at + bar.length - 1e-6);
      const onsets = [...new Set(inBar.map((event) => event.at))].sort((a, b) => a - b).slice(0, 16);
      const sounded = onsets.map((onset) => inBar.filter((event) => event.at === onset).map((event) => event.pitch).sort((a, b) => a - b).map(noteName).join("+"));
      lines.push(`bar ${index + 1}: ${sounded.join(" ") || "rest"}`);
    });
  }
  return lines.join("\n");
}
